import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowPathIcon,
  ClockIcon,
  InboxIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import ApiLoader from '../../ui/ApiLoader';
import Pagination from '../../ui/pagination';

const CustomersTab = ({
  loading,
  customers,
  appliedSearchTerm,
  appliedFilters,
  statusOptions,
  industryOptions,
  setSelectedCustomer,
  setShowViewDetailsModal,
  setRenewalCustomer,
  setRenewalFormData,
  setShowRenewalModal,
  handleViewRenewalHistory,
  customerPage,
  setCustomerPage,
  customerLimit,
  customerTotal,
  hasMoreCustomers,
  loadingMoreCustomers,
  loadCustomers,
  onApplyFilters,
  onResetFilters
}) => {
  // Local UI state – updated as user types/selects, no API call
  const [localSearch, setLocalSearch] = useState(appliedSearchTerm || '');
  const [localFilters, setLocalFilters] = useState({
    status: appliedFilters?.status || '',
    industry: appliedFilters?.industry || '',
  });

  const handleApply = () => {
    onApplyFilters(localSearch, {
      ...appliedFilters,
      status: localFilters.status,
      industry: localFilters.industry,
    });
  };

  const handleReset = () => {
    setLocalSearch('');
    setLocalFilters({ status: '', industry: '' });
    onResetFilters();
  };

  // Check if local UI differs from applied (to hint user to click Apply)
  const hasUnappliedChanges =
    localSearch !== (appliedSearchTerm || '') ||
    localFilters.status !== (appliedFilters?.status || '') ||
    localFilters.industry !== (appliedFilters?.industry || '');

  const hasActiveFilters = appliedSearchTerm || appliedFilters?.status || appliedFilters?.industry;

  // Client-side filtering using applied (committed) values
  const filteredCustomers = useMemo(() => {
    return customers.filter((u) => {
      // Search filter
      if (appliedSearchTerm) {
        const term = appliedSearchTerm.toLowerCase();
        const fullName = `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase();
        const matchesSearch =
          fullName.includes(term) ||
          (u.email || '').toLowerCase().includes(term) ||
          (u.company || '').toLowerCase().includes(term) ||
          (u.phone || '').toLowerCase().includes(term) ||
          (u.location || '').toLowerCase().includes(term);
        if (!matchesSearch) return false;
      }
      // Status filter
      if (appliedFilters?.status && (u.status || '').toLowerCase() !== appliedFilters.status.toLowerCase()) {
        return false;
      }
      // Industry filter
      if (appliedFilters?.industry && (u.industry || '').toLowerCase() !== appliedFilters.industry.toLowerCase()) {
        return false;
      }
      return true;
    });
  }, [customers, appliedSearchTerm, appliedFilters?.status, appliedFilters?.industry]);

  if (loading && customers.length === 0) {
    return (
      <ApiLoader label="Loading customers..." className="py-20" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4 sm:mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold flex items-center">
              <UserGroupIcon className="w-6 h-6 text-blue-500 mr-2" />
              Customers
            </h2>
            <p className="text-gray-600 mt-1 text-xs sm:text-base">
              Converted customers with active accounts and platform access
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:grid sm:grid-cols-6 gap-2 sm:gap-4 mb-2 sm:mb-4">
          <div className="sm:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs sm:text-sm"
              />
            </div>
          </div>

          <select
            value={localFilters.status}
            onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
            className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs sm:text-sm"
          >
            <option value="">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <select
            value={localFilters.industry}
            onChange={(e) => setLocalFilters({ ...localFilters, industry: e.target.value })}
            className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs sm:text-sm"
          >
            <option value="">All Industries</option>
            {industryOptions.map((industry) => (
              <option key={industry.value} value={industry.value}>
                {industry.label}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={handleApply}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                hasUnappliedChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              Apply Filter
            </button>
            {hasActiveFilters && (
              <button
                onClick={handleReset}
                className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Active filter tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {appliedSearchTerm && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                Search: "{appliedSearchTerm}"
              </span>
            )}
            {appliedFilters?.status && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700">
                Status: {statusOptions.find(s => s.value === appliedFilters.status)?.label || appliedFilters.status}
              </span>
            )}
            {appliedFilters?.industry && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700">
                Industry: {industryOptions.find(i => i.value === appliedFilters.industry)?.label || appliedFilters.industry}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Customer Cards (Team Member style) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
        {filteredCustomers.length > 0 ? (
          filteredCustomers.map((u) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-800 px-4 py-4 flex flex-col gap-2 min-h-[320px] min-w-[0]"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-lg shadow-sm">
                  {(u.firstname?.[0] || "").toUpperCase() + (u.lastname?.[0] || "").toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{u.firstname + " "+u.lastname}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{u.role}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{u.company}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 divide-y divide-gray-100 dark:divide-gray-800 mb-2">
                {u.email && (
                  <div className="flex items-center gap-2 pt-2 text-xs text-gray-700 dark:text-gray-200 break-all">
                    <EnvelopeIcon className="w-4 h-4" />
                    <span className="break-all whitespace-pre-line">{u.email}</span>
                  </div>
                )}
                {u.phone && (
                  <div className="flex items-center gap-2 pt-2 text-xs text-gray-700 dark:text-gray-200">
                    <PhoneIcon className="w-4 h-4" />
                    <span>{u.phone}</span>
                  </div>
                )}
                {u.location && (
                  <div className="flex items-center gap-2 pt-2 text-xs text-gray-700 dark:text-gray-200">
                    <MapPinIcon className="w-4 h-4" />
                    <span>{u.location}</span>
                  </div>
                )}
                {u.renewal_date && (
                  <div className="flex items-center gap-2 pt-2 text-xs text-gray-700 dark:text-gray-200">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="font-medium">Renewal:</span>
                    <span>{new Date(u.renewal_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-between items-center gap-2 mt-auto pt-2">
                <button
                  onClick={() => {
                    setSelectedCustomer(u);
                    setShowViewDetailsModal(true);
                  }}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  View Details
                </button>

                {/* View Renewal History button */}
                <button
                  onClick={() => handleViewRenewalHistory(u)}
                  className="inline-flex items-center justify-center px-3 py-1 border border-blue-300 dark:border-blue-600 rounded-md text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                  title="View renewal history"
                >
                  <ClockIcon className="w-4 h-4" />
                </button>

                {/* Show Renew button if renewal is in current month */}
                {(() => {
                  if (!u.renewal_date) return null;
                  const renewalDate = new Date(u.renewal_date);
                  const today = new Date();
                  const currentMonth = today.getMonth();
                  const currentYear = today.getFullYear();
                  const renewalMonth = renewalDate.getMonth();
                  const renewalYear = renewalDate.getFullYear();
                  
                  if (renewalMonth === currentMonth && renewalYear === currentYear) {
                    return (
                      <button
                        onClick={() => {
                          setRenewalCustomer(u);
                          // Calculate default renewal date (1 year from current renewal date)
                          const currentRenewalDate = new Date(u.renewal_date);
                          const defaultNewRenewalDate = new Date(currentRenewalDate);
                          defaultNewRenewalDate.setFullYear(defaultNewRenewalDate.getFullYear() + 1);
                          
                          setRenewalFormData({
                            employeecount: u.employeecount || '',
                            price: u.price || '',
                            renewal_date: defaultNewRenewalDate.toISOString().split('T')[0]
                          });
                          setShowRenewalModal(true);
                        }}
                        className="inline-flex items-center justify-center px-3 py-1 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                        title="Renew subscription"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center py-16">
            <InboxIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No records found</p>
            {hasActiveFilters && (
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                Try adjusting your search or filter criteria
              </p>
            )}
          </div>
        )}
      </div>

      <Pagination
        currentPage={customerPage}
        totalPages={Math.max(1, Math.ceil(customerTotal / customerLimit))}
        totalCount={customerTotal}
        pageSize={customerLimit}
        onPageChange={(page) => {
          setCustomerPage(page);
          loadCustomers(page);
        }}
        loading={loadingMoreCustomers}
      />
    </div>
  );
};

export default CustomersTab;
